// @ts-check

import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { collectCoreBomScopes } from '../objects/group_service.js?v=20260904c';

function isPlainRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function reportBomHookFailure(systemId, hook, error) {
  console.warn(`[bom] System-Hook ${hook} ist fehlgeschlagen`, systemId, error);
}

function normalizeBomRowsForOwner(rows, ownerSystemId) {
  if (!Array.isArray(rows)) return [];
  const normalized = [];
  for (const row of rows) {
    if (!isPlainRecord(row)) continue;
    try {
      normalized.push({
        ...row,
        sourceSystemId: ownerSystemId
      });
    } catch (error) {
      reportBomHookFailure(ownerSystemId, 'createBomRows:row', error);
    }
  }
  return normalized;
}

function normalizedIds(values) {
  if (!Array.isArray(values) && !(values instanceof Set)) return [];
  return [...new Set([...values]
    .map(value => String(value || '').trim())
    .filter(Boolean))];
}

/**
 * Sammelt collect Scopes im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectScopes() {
  const byId = new Map();
  const coreSystems = new Set();
  for (const scope of collectCoreBomScopes()) {
    if (!scope?.bounds || !scope?.id) continue;
    byId.set(scope.id, scope);
    if (scope.systemId) coreSystems.add(scope.systemId);
  }
  for (const scope of SystemRegistry.collectBomScopes()) {
    if (scope?.systemId && coreSystems.has(scope.systemId)) continue;
    if (!scope?.bounds || !scope?.id || byId.has(scope.id)) continue;
    byId.set(scope.id, scope);
  }
  return [...byId.values()];
}

/**
 * Beschreibt combine Bounds im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} scopes - Eingabewert für combine Bounds.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function combineBounds(scopes = []) {
  const boxes = (scopes || []).map(scope => scope.bounds).filter(Boolean);
  if (!boxes.length) return { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
  return {
    minX: Math.min(...boxes.map(box => box.minX)),
    minZ: Math.min(...boxes.map(box => box.minZ)),
    maxX: Math.max(...boxes.map(box => box.maxX)),
    maxZ: Math.max(...boxes.map(box => box.maxZ))
  };
}

/**
 * Beschreibt scopes For Ids im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} scopeIds - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function scopesForIds(scopeIds = []) {
  const ids = new Set(scopeIds || []);
  return collectScopes().filter(scope => ids.has(scope.id));
}

/**
 * Beschreibt scope Ids By System im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} scopes - Eingabewert für scope Ids By System.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function scopeIdsBySystem(scopes = []) {
  const map = {};
  for (const scope of scopes || []) {
    if (!scope?.systemId) continue;
    if (!Array.isArray(map[scope.systemId])) map[scope.systemId] = [];
    const ids = Array.isArray(scope.bomIds)
      ? scope.bomIds
      : scope.sourceId
        ? [scope.sourceId]
        : [];
    map[scope.systemId].push(...ids);
  }
  return map;
}

/**
 * Beschreibt rows For System im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} scopeIds - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function rowsForSystem(systemId, scopeIds = []) {
  const system = SystemRegistry.getSystem(systemId);
  if (!system) return [];
  const createRows = system.getBomItems || system.createBomRows;
  if (typeof createRows !== 'function') return [];
  try {
    return normalizeBomRowsForOwner(createRows(scopeIds), system.id);
  } catch (error) {
    reportBomHookFailure(system.id, 'createBomRows', error);
    return [];
  }
}

/**
 * Sammelt collect Rows im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} scopeBySystem - Eingabewert für collect Rows.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectRows(scopeBySystem = {}) {
  return SystemRegistry.allSystems().flatMap(system => {
    let scopeIds;
    try {
      scopeIds = Array.isArray(scopeBySystem[system.id]) ? scopeBySystem[system.id] : [];
    } catch (error) {
      reportBomHookFailure(system.id, 'collectBomRows:scopeIds', error);
      return [];
    }
    if (!scopeIds.length) return [];
    return rowsForSystem(system.id, scopeIds);
  });
}

/**
 * Beschreibt rows For Scope Ids im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} scopeIds - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function rowsForScopeIds(scopeIds = []) {
  return scopesForIds(scopeIds).flatMap(scope => {
    const bomIds = Array.isArray(scope.bomIds)
      ? scope.bomIds
      : scope.sourceId
        ? [scope.sourceId]
        : [];
    const rows = rowsForSystem(scope.systemId, bomIds);
    return rows.map(row => ({
      ...row,
      bomScopeId: scope.id,
      bomGroupId: scope.groupId || scope.sourceId || scope.id,
      bomGroupName: scope.name || scope.label || row.bomGroupName || scope.id,
      sourceSystemId: row.sourceSystemId || scope.systemId
    }));
  });
}

/**
 * Sammelt collect Plan Selection Keys im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectBomPlanSystemIds() {
  const systemIds = new Set();
  for (const scope of collectScopes()) {
    const systemId = String(scope?.systemId || '').trim();
    if (systemId) systemIds.add(systemId);
  }
  return Array.from(systemIds);
}

/**
 * Beschreibt plan Selection For Scopes im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Artikel- und Stücklistenlogik.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} scopes - Eingabewert für plan Selection For Scopes.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function renderSelectionForScopes(scopes = []) {
  const map = new Map();
  for (const systemId of collectBomPlanSystemIds()) map.set(systemId, new Set());
  for (const scope of scopes || []) {
    const systemId = String(scope?.systemId || '').trim();
    if (!systemId) continue;
    if (!map.has(systemId)) map.set(systemId, new Set());
    let renderIds;
    try {
      renderIds = normalizedIds(scope.renderIds);
    } catch (error) {
      reportBomHookFailure(systemId, 'renderIds', error);
      continue;
    }
    for (const id of renderIds) map.get(systemId).add(id);
  }
  return map;
}
